import React, { useEffect, useState, ReactElement } from "react"
import { Select, OptionComponent, OptionComponentProps, Text } from "@carrotfertility/carotene"
import { PageOption } from "components/dynamic-content/util/dynamicContent"
import { GuidanceElement } from "lib/contentful/components/GuidanceElement"
import { Paths } from "utils/Paths"
import { Redirect, Route, Switch, useHistory } from "react-router-dom"
import TalkToCarrotLocationRouter from "components/talk-to-carrot/TalkToCarrotLocationRouter"
import { TalkToCarrotContext } from "components/talk-to-carrot/TalkToCarrotContext"
import TwoColumnRightPrimary from "components/views/atoms/layouts/TwoColumnRightPrimary"
import { Large, Medium, NotSmall, Small } from "components/views/atoms/Responsive"
import styled from "styled-components"
import {
  getGetYourCarrotPlanPath,
  getHelpPath,
  getProviderEligibilityPath,
  getRefreshCarrotPlanPath,
  HelpNode,
  NeedHelpLink,
  RootBreadcrumb,
  SmallHeaderOverride
} from "components/talk-to-carrot/util/talkToCarrotMolecules"
import { HelpPage } from "components/talk-to-carrot/TalkToCarrotNeedHelp"
import { getTTCLocationOption } from "lib/contentful/utils/mappingUtils"
import { TalkToCarrotLegal } from "components/talk-to-carrot/TalkToCarrotLegal"
import { getTalkToCarrotLocationGuidance, getTalkToCarrotLocations } from "../../lib/contentful/contentfulClient"
import { ButtonPrimary } from "../../components/views/atoms/buttons/ButtonPrimary"
import WorldVectorMap from "../../components/talk-to-carrot/TalkToCarrotMap"
import Guidance from "../../components/views/atoms/Guidance"
import { PageHeader } from "../../components/views/molecules/Molecules"
import { MasterLayout } from "../../components/views/atoms/layouts/MasterLayout"
import { Spacer } from "../../components/views/atoms/Atoms"
import { ProviderEligibility } from "./provider-eligibility"
import { RefreshCarrotPlan } from "./refresh-carrot-plan"
import { GetYourCarrotPlan } from "./get-your-carrot-plan"

const LocationFooter = styled.div.attrs({ className: "flex items-center w-100 justify-center" })`
  overflow: auto;
`
const sortByLabel = ({ label: labelA }, { label: labelB }): 0 | 1 | -1 => {
  if (labelA < labelB) {
    return -1
  }
  if (labelA > labelB) {
    return 1
  }
  return 0
}

const SelectedOption = styled.div`
  background-color: rgba(228, 132, 0, 0.05);
`
const SelectedSpacer = styled.div`
  display: flex;
  border-bottom: 2px solid #f5f5f5;
  margin: 19px 16px;
`

const TalkToCarrotLocations = (): JSX.Element => {
  const history = useHistory()
  const [talkToCarrotLocations, setTalkToCarrotLocations] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState<PageOption>(null)
  const [hoveredLocation, setHoveredLocation] = useState<PageOption>(null)
  const [guidance, setGuidance] = useState<ReactElement<typeof Guidance>>(null)

  const addHover = (option: PageOption): PageOption => {
    return {
      ...option,
      onMouseOver: (): void => setHoveredLocation(option),
      onMouseOut: (): void => setHoveredLocation(null)
    }
  }

  useEffect(() => {
    const loadTalkToCarrotLocations = async (): Promise<void> => {
      const talkToCarrotLocations = await getTalkToCarrotLocations()
      const talkToCarrotLocationOptions = talkToCarrotLocations
        ?.map(getTTCLocationOption)
        .map(addHover)
        .filter(Boolean)
        .sort(sortByLabel)
      setTalkToCarrotLocations(talkToCarrotLocationOptions)
    }
    if (!talkToCarrotLocations) {
      loadTalkToCarrotLocations()
    }

    const loadTalkToCarrotLocationGuidanceProps = async (): Promise<void> => {
      const guidance = await getTalkToCarrotLocationGuidance()
      setGuidance(<GuidanceElement guidance={guidance} />)
    }
    if (!guidance) {
      loadTalkToCarrotLocationGuidanceProps()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = (): void => {
    history.push(`${Paths.TALK_TO_CARROT}/${selectedLocation.slug}`)
  }

  const selectedOptionToTop = (locationOption: PageOption): void => {
    setTalkToCarrotLocations(
      locationOption
        ? [
            locationOption,
            ...talkToCarrotLocations.filter(({ label }) => label !== locationOption.label).sort(sortByLabel)
          ]
        : talkToCarrotLocations
    )
  }

  const updateSelect = (locationOption): void => {
    setHoveredLocation(null)
    setSelectedLocation(locationOption)
    selectedOptionToTop(locationOption)
  }

  const continueButton = (
    <div className={"ml-auto"}>
      <ButtonPrimary onClick={onSubmit} enabled={!!selectedLocation}>
        {"Continue"}
      </ButtonPrimary>
    </div>
  )

  const continueWithHelp = (
    <>
      {<NeedHelpLink baseUrl={Paths.TALK_TO_CARROT} />}
      {continueButton}
    </>
  )

  const Option = (props: OptionComponentProps): JSX.Element => {
    return (
      <div onMouseOver={props.data.onMouseOver} onMouseOut={props.data.onMouseOut}>
        {props.isSelected ? (
          <>
            <SelectedOption>
              <OptionComponent {...props}>
                <Text weight="bold">{props.label}</Text>
              </OptionComponent>
            </SelectedOption>
            <SelectedSpacer />
          </>
        ) : (
          <OptionComponent {...props}>
            <Text>{props.label}</Text>
          </OptionComponent>
        )}
      </div>
    )
  }

  const locationSelector = (
    <Select
      name={"location"}
      options={talkToCarrotLocations}
      onChange={updateSelect}
      value={selectedLocation}
      optionComponent={Option}
      isClearable
      backspaceRemovesValue
    />
  )
  const PickerFooter = ({ withHelp = false }: { withHelp?: boolean }): JSX.Element => (
    <LocationFooter>{withHelp ? continueWithHelp : continueButton}</LocationFooter>
  )

  const map = (
    <NotSmall>
      <WorldVectorMap
        selectedLocationId={selectedLocation ? selectedLocation.value.toLowerCase() : ""}
        hoveredLocationId={hoveredLocation ? hoveredLocation.value.toLowerCase() : ""}
        onMouseEnter={({ target }: any): void =>
          setHoveredLocation(talkToCarrotLocations.find(({ value }) => value.toLowerCase() === target.id.toLowerCase()))
        }
        onMouseLeave={(): void => setHoveredLocation(null)}
        onClick={({ target }: any): void => {
          const optionClicked = talkToCarrotLocations.find(
            ({ value }) => value.toLowerCase() === target.id.toLowerCase()
          )
          if (optionClicked) {
            updateSelect(optionClicked)
          }
        }}
      />
    </NotSmall>
  )

  const locationPicker = (
    <div className="w-100">
      {map}
      {locationSelector}
      <PickerFooter />
    </div>
  )
  const locationPickerWithHelp = (
    <div className="w-100">
      {map}
      {locationSelector}
      <PickerFooter withHelp />
    </div>
  )

  const GuidanceWithHelp = ({ guidance }: { guidance: ReactElement<typeof Guidance> }): JSX.Element => (
    <>
      {guidance}
      {<HelpNode baseUrl={Paths.TALK_TO_CARROT} />}
    </>
  )

  const guidedLocationPicker = (
    <>
      <MasterLayout
        footer={<TalkToCarrotLegal />}
        header={<PageHeader icon={"icn-talk-to-carrot"} title={"Talk to Carrot"} />}
      >
        {guidance && (
          <>
            <Large>
              <TwoColumnRightPrimary
                secondaryContent={<GuidanceWithHelp guidance={guidance} />}
                primaryContent={locationPicker}
              />
            </Large>
            <Medium>
              {guidance}
              <Spacer height={3} />
              {locationPickerWithHelp}
            </Medium>
            <Small>
              <SmallHeaderOverride>
                {guidance}
                <Spacer height={2} />
                {locationPickerWithHelp}
              </SmallHeaderOverride>
            </Small>
          </>
        )}
      </MasterLayout>
    </>
  )

  return (
    <>
      {talkToCarrotLocations && (
        <TalkToCarrotContext.Provider value={{ talkToCarrotLocationOptions: talkToCarrotLocations }}>
          <Switch>
            <Route exact path={`${Paths.TALK_TO_CARROT}`} render={(): JSX.Element => guidedLocationPicker} />
            <Route
              exact
              path={getHelpPath({ baseUrl: Paths.TALK_TO_CARROT })}
              render={(): JSX.Element => (
                <HelpPage breadcrumbs={[<RootBreadcrumb key="rootBreadcrumb" />]} baseUrl={Paths.TALK_TO_CARROT} />
              )}
            />
            <Route
              exact
              path={getProviderEligibilityPath({ baseUrl: Paths.TALK_TO_CARROT })}
              render={(): JSX.Element => (
                <ProviderEligibility
                  breadcrumbs={[<RootBreadcrumb key="rootBreadcrumb" />]}
                  baseUrl={Paths.TALK_TO_CARROT}
                />
              )}
            />
            <Route
              exact
              path={getRefreshCarrotPlanPath({ baseUrl: Paths.TALK_TO_CARROT })}
              render={(): JSX.Element => (
                <RefreshCarrotPlan
                  breadcrumbs={[<RootBreadcrumb key="rootBreadcrumb" />]}
                  baseUrl={Paths.TALK_TO_CARROT}
                />
              )}
            />
            <Route
              exact
              path={getGetYourCarrotPlanPath({ baseUrl: Paths.TALK_TO_CARROT })}
              render={(): JSX.Element => <GetYourCarrotPlan baseUrl={Paths.TALK_TO_CARROT} />}
            />
            <Route path={`${Paths.TALK_TO_CARROT}/:locationSlug`} component={TalkToCarrotLocationRouter} />
            <Redirect from="*" to="/talk-to-carrot" />
          </Switch>
        </TalkToCarrotContext.Provider>
      )}
    </>
  )
}

export { TalkToCarrotLocations }
